'use client';

import * as React from 'react';
import {
  BarChart as RechartsBarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
} from 'recharts';

// Shadcn chart primitives (`ChartContainer` provides ChartContext — distinct from `ChartFrame`).
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/hen-ui/shadcn/chart';

import { cn, composeRefs } from '@/hen-ui/utils';

import { ChartLegend } from '../ChartLegend';
import {
  getSafeChartProps,
  useMissingChartConfigWarning,
  buildSafeSeriesKeyMap,
  renderChartTick,
  deriveValueAxisDefaults,
  resolveTooltipValueFormat,
  validateTooltipMode,
  renderTooltipFooter,
  usePinnedTooltip,
} from '../chart-utils';
import {
  withSeriesColorScaleDefaults,
  resolveDivergingArmColor,
} from '../chart-palette';
import { getDynamicAxisWidth } from '@/hen-ui/utils/chart';
import type {
  ChartDataPoint,
  SeriesColorConfig,
  ChartTooltipMode,
  ChartTooltipFooter,
  ColorScale,
  SequentialPalette,
  DivergingPalette,
} from '../chart-types';
import {
  resolveTooltipFormat,
  resolveValueFormat,
  type TooltipFormat,
  type ValueFormat,
} from '../number-format';
import {
  evaluateColorRules,
  resolveSeriesBaseColor,
  withSemanticLegendColors,
  buildTooltipIndicatorColorResolver,
} from '../chart-color-rules';

export interface WaterfallChartProps {
  /**
   * Pre-structured data rows. Each row carries the category key plus values
   * for every entry in `series`. Convention: each row supplies BOTH series
   * keys; the unused side is `0` or `null`. The row-shaper splits a single Δ
   * into the appropriate positive/negative slot so the legend renders cleanly
   * with two entries.
   */
  data: ChartDataPoint[];
  /** Maps each series key to label + color (shadcn ChartConfig). Drives legend + tooltip + bar fill. */
  config: ChartConfig;
  /** Exactly two keys: [positiveKey, negativeKey]. The component renders one <Bar> per key. */
  series: [string, string];
  /** Key in each data row used for the category (Y) axis. */
  xAxisKey: string;
  /** Label displayed alongside the value (X) axis. */
  xAxisLabel?: string;
  /** Label displayed alongside the category (Y) axis. */
  yAxisLabel?: string;
  /** Fixed value-axis range, e.g. [-12, 12]. Defaults to Recharts auto-domain. */
  xAxisDomain?: [number, number];
  /** Specific tick values on the value axis, e.g. [-12, -8, -4, 0, 4, 8, 12]. */
  xAxisTicks?: number[];
  /** Formatter for value-axis tick labels (e.g. `(v) => `${v}%``). */
  xAxisFormat?: ValueFormat;
  /**
   * Formatter for the numeric value rendered in the tooltip. Receives the
   * series name so positive/negative series can format independently.
   * Defaults to `xAxisFormat` if not provided.
   */
  tooltipValueFormat?: (value: number, name: string | number) => string;
  /**
   * Controls when the data-point tooltip is shown.
   * - `'hover'`    — standard hover tooltip (default).
   * - `'pinned'`   — tooltip appears on click and stays until the user
   *                  clicks elsewhere on the chart or presses Escape.
   * - `'disabled'` — tooltip is suppressed entirely.
   */
  tooltipMode?: ChartTooltipMode;
  /**
   * Row-aware value formatter for the tooltip. Receives the numeric value,
   * the series key, and the full raw data row. Takes precedence over
   * `tooltipValueFormat` when both are provided.
   */
  tooltipFormatter?: TooltipFormat;
  /**
   * Methodology copy rendered as a footer note inside the tooltip bubble.
   * - Plain `string`    — rendered verbatim.
   * - `{ md: string }` — parsed for `**bold**`, `*italic*`, `[text](url)`.
   */
  tooltipContent?: ChartTooltipFooter;
  /**
   * Chart height in px (default 300).
   *
   * - With `fillContainer={false}` (default): fixed at exactly this many pixels.
   * - With `fillContainer={true}`: acts as the **minimum** height. The chart
   *   contributes this value upward to the layout's auto-sizing chain (so the
   *   parent grid row is at least this tall) and stretches beyond it via `h-full`
   *   when a sibling makes the row taller.
   */
  height?: number;
  /**
   * When true, the chart fills its parent container's height. The parent must
   * provide a definite height context (e.g. a flex column where this chart is
   * `flex-1`, or a grid cell). `height` continues to act as the minimum floor.
   */
  fillContainer?: boolean;
  /** Maximum width of a single bar in px (default 32). */
  maxBarSize?: number;
  /** Border radius for bar corners in px (default 4). */
  barRadius?: number;
  /** Show legend below chart (default true). */
  showLegend?: boolean;
  /**
   * Override the default character-count limit (30) used when truncating
   * category-axis labels. Values below 4 are ignored (falls back to the
   * library default). Useful for charts with long segment names.
   */
  xAxisLabelMaxLength?: number;
  /**
   * Per-series color configuration for conditional coloring on waterfall
   * segments. `auto-by-value` maps positive → success, negative → error.
   * Color rules evaluate per bar segment value.
   */
  seriesColorConfigs?: Record<string, SeriesColorConfig>;
  /**
   * Color-scale mode for the bar colors (default `'categorical'`). Setting
   * `'diverging'` decouples the historical hardcoded positive/negative palette
   * slots and sources the two bar colors from the chosen diverging family's
   * high (positive) and low (negative) arms. Omitting this renders
   * byte-identically to the categorical default. Semantic rules
   * (`seriesColorConfigs`) and explicit `config[key].color` still win.
   */
  colorScale?: ColorScale;
  /** Sequential palette family when `colorScale='sequential'` (default `'blues'`). */
  sequentialPalette?: SequentialPalette;
  /** Diverging palette family when `colorScale='diverging'` (default `'blueOrange'`). */
  divergingPalette?: DivergingPalette;
}

const EMPTY_FALLBACK_TEXT = 'No data available';

/**
 * WaterfallChart — diverging horizontal bar chart for change-by-segment views.
 *
 * Each bar represents one segment, anchored at a zero reference line and
 * extending right (positive Δ) or left (negative Δ). Built on Recharts via the
 * shadcn chart primitive; theme-aware via `ChartConfig` + scoped
 * `--color-<key>` CSS variables.
 *
 * Two `<Bar>` series (one positive, one negative) are rendered so Recharts
 * emits a clean two-entry legend; consumers split a single Δ value into the
 * appropriate positive/negative slot per row. Recharts handles negative values
 * natively — no stacking math is needed.
 *
 * Pure renderer: no async state, no domain types, no title (`ChartFrame` owns
 * title/subtitle on the consumer side).
 */
export const WaterfallChart = React.forwardRef<
  HTMLDivElement,
  WaterfallChartProps
>(
  (
    {
      data: rawData,
      config,
      series,
      xAxisKey,
      xAxisLabel,
      yAxisLabel,
      xAxisDomain: rawDomain,
      xAxisTicks: rawTicks,
      xAxisFormat: rawXAxisFormat,
      height = 300,
      fillContainer = false,
      maxBarSize = 32,
      barRadius = 4,
      showLegend = true,
      xAxisLabelMaxLength,
      seriesColorConfigs,
      colorScale = 'categorical',
      sequentialPalette,
      divergingPalette,
      tooltipValueFormat,
      tooltipMode,
      tooltipFormatter: rawTooltipFormatter,
      tooltipContent,
    },
    ref,
  ) => {
    const xAxisFormat = React.useMemo(
      () => resolveValueFormat(rawXAxisFormat),
      [rawXAxisFormat],
    );
    const tooltipFormatter = React.useMemo(
      () => resolveTooltipFormat(rawTooltipFormatter, xAxisFormat),
      [rawTooltipFormatter, xAxisFormat],
    );

    // `getSafeChartProps` keys are generic over Recharts mutation safety,
    // not over orientation — for a horizontal bar chart, the value axis is X
    // but the helper's `yAxisDomain` / `yAxisTicks` slots still apply.
    const {
      data,
      yAxisDomain: xAxisDomain,
      yAxisTicks: xAxisTicks,
    } = getSafeChartProps({
      data: rawData,
      yAxisDomain: rawDomain,
      yAxisTicks: rawTicks,
    });

    // Sanitize series keys into CSS-custom-property-safe tokens. Human-readable
    // labels like "Net Change" produce `--color-Net Change` — an invalid CSS
    // declaration that the browser drops, leaving bars black. `dataKey` on
    // each <Bar> keeps the raw key so Recharts reads the correct data column;
    // `name`, config keys, and `var(--color-*)` references all use the safe token.
    const safeKeyMap = React.useMemo(
      () => buildSafeSeriesKeyMap(series),
      [series],
    );
    const safeSeries = React.useMemo(
      () => series.map((k) => safeKeyMap.get(k) ?? k),
      [series, safeKeyMap],
    );
    const safeConfig = React.useMemo<ChartConfig>(() => {
      const next: ChartConfig = {};
      for (const rawKey of series) {
        const safeKey = safeKeyMap.get(rawKey) ?? rawKey;
        const entry = config[rawKey];
        if (entry) {
          next[safeKey] =
            safeKey !== rawKey
              ? { ...entry, label: entry.label ?? rawKey }
              : entry;
        } else if (safeKey !== rawKey) {
          next[safeKey] = { label: rawKey };
        }
      }
      return next;
    }, [series, config, safeKeyMap]);
    const safeSeriesColorConfigs = React.useMemo<
      Record<string, SeriesColorConfig> | undefined
    >(() => {
      if (!seriesColorConfigs) return undefined;
      const next: Record<string, SeriesColorConfig> = {};
      for (const [rawKey, val] of Object.entries(seriesColorConfigs)) {
        next[safeKeyMap.get(rawKey) ?? rawKey] = val;
      }
      return next;
    }, [seriesColorConfigs, safeKeyMap]);

    useMissingChartConfigWarning('WaterfallChart', config, series);

    // Auto-fill missing per-series colors based on `colorScale`. Categorical
    // (default) keeps the historical slot-a (Blue) / slot-b (Orange) mapping,
    // byte-identical to before. `diverging` decouples those hardcoded slots and
    // seeds the high-arm base color; the per-cell sign mapping below paints the
    // positive bar with the high arm and the negative bar with the low arm.
    // Explicit `config[key].color` still wins.
    const resolvedConfig = React.useMemo(
      () =>
        withSeriesColorScaleDefaults(safeConfig, safeSeries, {
          colorScale,
          sequentialPalette,
          divergingPalette,
        }),
      [safeConfig, safeSeries, colorScale, sequentialPalette, divergingPalette],
    );

    const isDiverging = colorScale === 'diverging';

    // Pre-resolve `semanticColor: 'auto-by-value'` to a concrete sign for
    // the legend path only. WaterfallChart's contract guarantees each
    // series is sign-pure (`series[0]` only carries ≥0 values,
    // `series[1]` only carries ≤0 values), so the generic
    // `auto-by-value` legend expansion — which emits BOTH a positive and
    // a negative swatch per series — would advertise colors the chart
    // can never render for that side. Collapsing to a single concrete
    // semantic per side avoids the "Positive, Positive (Negative),
    // Negative (Positive), Negative" four-entry uncanny legend.
    //
    // Same collapse is applied when a series declares `colorRules`
    // without a `semanticColor`: `expandLegendEntries`' base-entry
    // dedup only fires when `semanticColor` is set (palette CSS vars
    // are opaque and can't be compared against semantic tokens), so
    // without this we'd render a base palette swatch alongside the
    // rule swatch with identical labels post suffix-suppression
    // ("Positive" + "Positive"). Injecting the sign-anchored
    // `semanticColor` here triggers the existing dedup and collapses
    // each series back to its rendered rule colors only.
    //
    // The on-chart `<Cell>` path still consumes the original
    // `seriesColorConfigs` so per-cell evaluation is unchanged.
    const safeLegendSeriesColorConfigs = React.useMemo(() => {
      if (!safeSeriesColorConfigs) return safeSeriesColorConfigs;
      const [positiveSafeKey, negativeSafeKey] = safeSeries;
      const next: Record<string, SeriesColorConfig> = {
        ...safeSeriesColorConfigs,
      };
      const collapseSide = (
        key: string,
        sign: 'positive' | 'negative',
      ): void => {
        const cfg = safeSeriesColorConfigs[key];
        if (!cfg) return;
        if (cfg.semanticColor === 'auto-by-value') {
          next[key] = { ...cfg, semanticColor: sign };
          return;
        }
        // colorRules-only series → anchor the base entry to the matching
        // sign so `expandLegendEntries` can suppress the redundant palette
        // swatch. Skipped under a diverging scale: there the rest-state base
        // is the diverging *arm* color (resolved via `divergingArmByKey`),
        // not the green/red semantic token — so leave `semanticColor` unset
        // and let the arm branch in `expandLegendEntries` render it.
        if (
          !isDiverging &&
          cfg.semanticColor == null &&
          cfg.colorRules?.length
        ) {
          next[key] = { ...cfg, semanticColor: sign };
        }
      };
      collapseSide(positiveSafeKey, 'positive');
      collapseSide(negativeSafeKey, 'negative');
      return next;
    }, [safeSeriesColorConfigs, safeSeries, isDiverging]);

    // WaterfallChart's two series are sign-pure (`series[0]` carries the ≥0
    // arm, `series[1]` the <0 arm), so under a diverging scale each shows a
    // single arm swatch rather than both.
    const divergingArmByKey = React.useMemo<
      Record<string, 'low' | 'high'>
    >(() => {
      const [positiveSafeKey, negativeSafeKey] = safeSeries;
      return { [positiveSafeKey]: 'high', [negativeSafeKey]: 'low' };
    }, [safeSeries]);

    // Mirror per-series `semanticColor` overrides into the config the
    // `<ChartLegend>` reads from, so the legend swatch matches the rendered
    // bar color. We pass the *sign-resolved* `legendSeriesColorConfigs`
    // (not the raw prop) so `'auto-by-value'` series — which have no
    // single representative color — also get their swatch token
    // populated from the collapsed sign. Series with only `colorRules`
    // (no semanticColor, no auto-by-value) intentionally keep the base
    // palette color in the legend — per-rule entries are emitted by
    // `expandLegendEntries` inside `<ChartLegend>`.
    const legendConfig = React.useMemo(
      () =>
        withSemanticLegendColors(
          resolvedConfig,
          safeLegendSeriesColorConfigs,
          safeSeries,
        ),
      [resolvedConfig, safeLegendSeriesColorConfigs, safeSeries],
    );

    // EDUN-11255 — auto-derive value-axis (X) domain + ticks from the
    // diverging data when the consumer has not pinned both. `signed: true`
    // tells the deriver to keep the data-driven `[dataMin, dataMax]`
    // bounds instead of anchoring at zero — Waterfall is the diverging
    // case BarChart's monotonic-anchor contract explicitly excludes.
    const valueAxisDefaults = React.useMemo(() => {
      if (xAxisDomain && xAxisTicks) return undefined;

      // Each row supplies BOTH series keys per the contract; the unused
      // side is `0` or `null`, so flattening across keys gives every
      // signed delta exactly once without double-counting.
      const cellValues = data.flatMap((row) =>
        series.map((key) =>
          typeof row[key] === 'number' ? (row[key] as number) : NaN,
        ),
      );
      return deriveValueAxisDefaults(cellValues, { signed: true });
    }, [data, series, xAxisDomain, xAxisTicks]);

    const valueAxisDomain = xAxisDomain ??
      valueAxisDefaults?.domain ?? ['auto', 'auto'];
    const valueAxisTicks = xAxisTicks ?? valueAxisDefaults?.ticks;

    // Calculate dynamic axis width based on longest label to prevent truncation/wrapping.
    // This adapts the Y-axis width to the actual data. Also truncate extremely long
    // labels as a fallback to keep the chart focused on data.
    const { axisWidth } = getDynamicAxisWidth(data, xAxisKey);

    // In fillContainer mode the chart contributes `height` upward as min-height
    // (so the row is sized from content, same as fixed-pixel mode) and uses
    // `h-full` to stretch when a sibling makes the row taller. In fixed mode it
    // pins to exactly `height`.
    const sizingStyle: React.CSSProperties = fillContainer
      ? { minHeight: height }
      : { height };
    const sizingClass = fillContainer ? 'h-full' : undefined;

    // Store validated tooltip mode once per render to avoid duplicate validation and warnings.
    const resolvedTooltipMode = validateTooltipMode(tooltipMode);

    // Must be called before the early return (rules of hooks).
    const {
      tooltipActive,
      onChartClick,
      containerRef: pinnedRef,
    } = usePinnedTooltip(resolvedTooltipMode);
    const rootRef = React.useMemo(
      () => composeRefs<HTMLDivElement>(pinnedRef, ref),
      [pinnedRef, ref],
    );

    if (!data.length) {
      // See LineChart for rationale: intentionally no `role`, since this is
      // static text rather than an image or live region.
      return (
        <div
          ref={ref}
          aria-label={EMPTY_FALLBACK_TEXT}
          className={cn(
            'flex items-center justify-center rounded bg-neutral-50 text-neutral-500 text-b4',
            sizingClass,
          )}
          style={sizingStyle}
        >
          {EMPTY_FALLBACK_TEXT}
        </div>
      );
    }

    // `role='figure'` (not `role='img'`): `img` is a leaf role and prunes
    // the inner Recharts `role='application'` SVG surface and its
    // accessibilityLayer output, breaking keyboard-nav announcements.
    // See LineChart for full rationale.
    return (
      <div
        ref={rootRef}
        role='figure'
        aria-label='Diverging bar chart'
        className={sizingClass}
        style={sizingStyle}
      >
        <ChartContainer
          config={resolvedConfig}
          className='aspect-auto h-full w-full'
        >
          <RechartsBarChart
            data={data}
            layout='vertical'
            maxBarSize={maxBarSize}
            accessibilityLayer
            onClick={onChartClick}
          >
            <CartesianGrid strokeDasharray='3 3' />
            <XAxis
              type='number'
              height={xAxisLabel ? 50 : 30}
              domain={valueAxisDomain}
              ticks={valueAxisTicks}
              tickFormatter={xAxisFormat}
              label={
                xAxisLabel
                  ? { value: xAxisLabel, position: 'insideBottom', offset: -5 }
                  : undefined
              }
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              type='category'
              dataKey={xAxisKey}
              interval={0}
              tick={(p) =>
                renderChartTick({
                  ...p,
                  dy: '0.355em',
                  textAnchor: 'end',
                  maxLabelLength: xAxisLabelMaxLength,
                })
              }
              label={
                yAxisLabel
                  ? {
                      value: yAxisLabel,
                      angle: -90,
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' },
                    }
                  : undefined
              }
              tickLine={false}
              axisLine={false}
              width={axisWidth}
            />
            <ReferenceLine x={0} stroke='var(--neutral-400, #6B7280)' />
            {resolvedTooltipMode !== 'disabled' && (
              <ChartTooltip
                cursor={false}
                active={tooltipActive}
                trigger={resolvedTooltipMode === 'pinned' ? 'click' : undefined}
                content={
                  <ChartTooltipContent
                    indicator='dot'
                    valueFormatter={
                      tooltipFormatter
                        ? undefined
                        : resolveTooltipValueFormat(
                            tooltipValueFormat,
                            xAxisFormat,
                          )
                    }
                    rowValueFormatter={tooltipFormatter}
                    footer={renderTooltipFooter(tooltipContent)}
                    getItemIndicatorColor={buildTooltipIndicatorColorResolver(
                      safeSeriesColorConfigs,
                    )}
                  />
                }
              />
            )}
            {showLegend && (
              <ChartLegend
                series={safeSeries}
                config={legendConfig}
                seriesColorConfigs={safeLegendSeriesColorConfigs}
                colorScale={colorScale}
                divergingPalette={divergingPalette}
                divergingArmByKey={divergingArmByKey}
                verticalAlign='bottom'
                align='center'
                wrapperStyle={xAxisLabel ? { paddingTop: 12 } : undefined}
              />
            )}
            {series.map((rawKey, i) => {
              const safeKey = safeSeries[i];
              const cfg = safeSeriesColorConfigs?.[safeKey];
              const perCellCfg =
                cfg &&
                (!!cfg.colorRules?.length ||
                  cfg.semanticColor === 'auto-by-value')
                  ? cfg
                  : null;
              const needsPerCell = perCellCfg !== null || isDiverging;
              return (
                <Bar
                  key={safeKey}
                  dataKey={rawKey}
                  name={safeKey}
                  stackId='delta'
                  fill={resolveSeriesBaseColor(safeKey, safeSeriesColorConfigs)}
                  radius={[0, barRadius, barRadius, 0]}
                >
                  {needsPerCell &&
                    data.map((entry, index) => {
                      const value =
                        typeof entry[rawKey] === 'number'
                          ? (entry[rawKey] as number)
                          : null;
                      const defaultColor = isDiverging
                        ? (resolveDivergingArmColor(value, divergingPalette) ??
                          `var(--color-${safeKey})`)
                        : `var(--color-${safeKey})`;
                      return (
                        <Cell
                          key={index}
                          fill={evaluateColorRules(
                            cfg?.colorRules,
                            value,
                            cfg?.semanticColor,
                            defaultColor,
                          )}
                        />
                      );
                    })}
                </Bar>
              );
            })}
          </RechartsBarChart>
        </ChartContainer>
      </div>
    );
  },
);
WaterfallChart.displayName = 'WaterfallChart';
